import React from 'react';

export function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 container-fluid">
      <div className="px-8 py-12 mx-8">
        <h1 className="text-3xl font-bold text-center mb-8">Terms of Service</h1>
        
        {/* Table of Contents */}
        <div className="mb-12 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold mb-4">Table of Contents</h2>
          <ol className="space-y-2">
            <li><a href="#introduction" className="text-blue-600 hover:underline">1. Introduction</a></li>
            <li><a href="#beta-status" className="text-blue-600 hover:underline">2. Beta Status</a></li>
            <li><a href="#license" className="text-blue-600 hover:underline">3. License and Use Restrictions</a></li>
            <li><a href="#user-content" className="text-blue-600 hover:underline">4. User Content and Data Handling</a></li>
            <li><a href="#availability" className="text-blue-600 hover:underline">5. Service Availability and Support</a></li>
            <li><a href="#ip-rights" className="text-blue-600 hover:underline">6. Intellectual Property Rights</a></li>
            <li><a href="#disclaimers" className="text-blue-600 hover:underline">7. Disclaimers and Limitations of Liability</a></li>
            <li><a href="#termination" className="text-blue-600 hover:underline">8. Term and Termination</a></li>
            <li><a href="#general" className="text-blue-600 hover:underline">9. General Provisions</a></li>
            <li><a href="#contact" className="text-blue-600 hover:underline">10. Contact Information</a></li>
          </ol>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="prose prose-lg max-w-none">
            <div className="text-sm text-gray-500 mb-8">
              <p>Last Updated: 3/10/2025</p>
            </div>

            <section id="introduction">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>These Terms of Service ("Terms") govern your access to and use of Provafi (the "Service"), a web-based application for financial data processing and standardization. The Service is currently in beta testing and provided on an as-is basis without guarantees of stability or availability. By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>
            </section>

            <section id="beta-status" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">2. Beta Status</h2>
              
              <h3 className="text-xl font-medium mb-3">2.1 Experimental Service</h3>
              <p>The Service is in an experimental beta testing phase. Features, terms, and availability may change or be discontinued at any time without notice.</p>
              <p>No formal business entity has been established yet. Future legal structure may change.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">2.2 No Warranties & User Acknowledgment</h3>
              <p>You understand and accept that this is a beta test of an experimental service.</p>
              <p>The Service is provided as-is and without any guarantees regarding uptime, security, functionality, or accuracy.</p>
              <p>Data loss, service interruptions, security vulnerabilities, or errors may occur.</p>
              <p>By using the Service, you assume all risks and waive any claims against us for damages or disruptions.</p>
            </section>

            <section id="license" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">3. License and Use Restrictions</h2>
              
              <h3 className="text-xl font-medium mb-3">3.1 Limited License Grant</h3>
              <p>You are granted a limited, non-exclusive, non-transferable, revocable license to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Access and use the Service for its intended purpose.</li>
                <li>Upload and process financial data.</li>
                <li>Export financial statements.</li>
              </ul>
              <p>This license is personal to you and cannot be assigned or sublicensed.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">3.2 Prohibited Uses</h3>
              <p>You shall not:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Copy, modify, reverse engineer, decompile, or attempt to extract source code.</li>
                <li>Resell, sublicense, or redistribute access to the Service.</li>
                <li>Use the Service to develop a competing product.</li>
                <li>Remove proprietary notices or use unauthorized automated means to access the Service.</li>
                <li>Violate any laws or regulations through use of the Service.</li>
              </ul>
            </section>

            <section id="user-content" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">4. User Content and Data Handling</h2>

              <h3 className="text-xl font-medium mb-3">4.1 Data Ownership</h3>
              <p>You retain all ownership rights to the financial data you upload.</p>
              <p>You grant us a limited license to store, process, and transmit your data solely to provide the Service.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">4.2 Security and Storage Risks</h3>
              <p>We use third-party cloud providers for data storage with default encryption standards.</p>
              <p>No system is 100% secure, and you accept the inherent risks of using a beta service.</p>
              <p>We do not guarantee data retention or recovery during the beta phase.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">4.3 Beta Data Upload Restrictions</h3>
              <p>You shall not upload:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Personally Identifiable Information (PII) or sensitive business information.</li>
                <li>Company-identifiable data, internal codes, or confidential financial details.</li>
                <li>Banking or financial account credentials.</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">4.4 Data Deletion Policy</h3>
              <p>You must export or delete your data before the beta period ends.</p>
              <p>We reserve the right to delete beta data at any time.</p>
              <p>If sensitive data is detected, we may immediately delete it and terminate your access.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">4.5 Privacy Policy</h3>
              <p>Our Privacy Policy outlines how we handle your data, including third-party service providers and data retention practices. The Privacy Policy is incorporated by reference into these Terms.</p>
            </section>

            <section id="availability" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">5. Service Availability and Support</h2>
              
              <h3 className="text-xl font-medium mb-3">5.1 No Guaranteed Uptime</h3>
              <p>We do not guarantee uninterrupted access to the Service.</p>
              <p>We may modify, suspend, or terminate the Service at any time.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">5.2 Limited Beta Support</h3>
              <p>Basic support is provided at our discretion during normal business hours.</p>
              <p>We do not guarantee responses or issue resolution timelines.</p>
            </section>

            <section id="ip-rights" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property Rights</h2>

              <h3 className="text-xl font-medium mb-3">6.1 Ownership & Restrictions</h3>
              <p>We retain all rights, title, and interest in the Service, including all intellectual property rights.</p>
              <p>You may not copy, modify, or create derivative works based on the Service.</p>
              <p>Reverse engineering, scraping, or automated data extraction is strictly prohibited.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">6.2 Feedback & Suggestions</h3>
              <p>Any feedback you provide may be used by us freely without compensation.</p>
            </section>

            <section id="disclaimers" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">7. Disclaimers and Limitations of Liability</h2>

              <h3 className="text-xl font-medium mb-3">7.1 No Warranties</h3>
              <p>The Service is provided "as is" and "as available," without warranties of any kind.</p>
              <p>We disclaim all warranties, including merchantability, fitness for a particular purpose, and non-infringement.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">7.2 Limitation of Liability</h3>
              <p>IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.</p>
              <p>OUR TOTAL LIABILITY SHALL NOT EXCEED $100 OR THE AMOUNT YOU HAVE PAID FOR THE SERVICE, WHICHEVER IS LOWER.</p>
            </section>

            <section id="termination" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">8. Term and Termination</h2>

              <h3 className="text-xl font-medium mb-3">8.1 Duration</h3>
              <p>These Terms remain in effect until terminated by either party.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">8.2 Termination Rights</h3>
              <p>We may suspend or terminate your access immediately if:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>You violate these Terms.</li>
                <li>The beta program ends.</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">8.3 Post-Termination Data Access</h3>
              <p>Upon termination, your license is revoked and you must cease using the Service.</p>
              <p>You may export your data within 30 days, after which it may be deleted.</p>
            </section>

            <section id="general" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">9. General Provisions</h2>

              <h3 className="text-xl font-medium mb-3">9.1 Governing Law & Jurisdiction</h3>
              <p>These Terms are governed by the laws of the State of Michigan.</p>
              <p>Disputes shall be resolved in Michigan courts.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">9.2 Modifications</h3>
              <p>We may update these Terms at any time. Continued use constitutes acceptance.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">9.3 Severability</h3>
              <p>If any provision is found unenforceable, the rest remain in effect.</p>

              <h3 className="text-xl font-medium mt-6 mb-3">9.4 Entire Agreement</h3>
              <p>These Terms constitute the entire agreement between you and us.</p>
            </section>

            <section id="contact" className="mt-12">
              <h2 className="text-2xl font-semibold mb-4">10. Contact Information</h2>
              <p>For questions about these Terms, contact us at: support@provafi.com</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Terms;
